import { ApexOptions } from "apexcharts"
import { ApexChartType, makeApexOptions } from "src/shared/config/DefaultApexOptions"

/*
    One date format for every Monitor x axis (MPN-1530 follow-up).

    Rachel: "can we streamline the format of the date shown on the x-axis for all charts?"

    She wrote DD/MM, and she means month first. Her own mockup's axis labels are 7/14,
    7/31, 8/1 -- 7/31 settles it, there is no 31st month -- and the bug report beside it
    says "a gap in the CPC trend between 8/11-8/13" for dates in August. Building literal
    day/month would have turned 8 November into 11/08 and misread every date on the page,
    so this is month/day and the ticket comment says so out loud.

    Axis only. Tooltips keep the long form, because a hover has room for "11 Aug 2026"
    and that is where someone goes when the short label is ambiguous.

    Scoped to the Monitor page rather than changed in DefaultApexOptions, which is shared
    with every other chart in the product. Restyling those was not asked for.
*/

/** Apex `datetimeFormatter`, for the charts whose x values are timestamps. */
export const MONTH_DAY_DATETIME = {
  year: "M/d",
  month: "M/d",
  day: "M/d",
  // Never show a time on a daily series: Apex switches to hour labels as soon as the
  // visible range is short enough, and "14:00" on a spend chart is meaningless.
  hour: "M/d",
  minute: "M/d",
} as const

/** Long form for tooltips, where there is room to be unambiguous. */
export const FULL_DATE_TOOLTIP = "d MMM yyyy"

/**
 * Month/day for the charts whose x values are category strings.
 *
 * Parses the ISO date by hand rather than through Date. `new Date("2026-08-11")` is
 * parsed as UTC midnight and then rendered in local time, which moves the label to 8/10
 * for anyone west of Greenwich -- the whole axis slips by a day.
 */
export function monthDay(value: string | number | Date | null | undefined): string {
  if (value == null || value === "") return ""

  if (typeof value === "string") {
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (iso) return `${Number(iso[2])}/${Number(iso[3])}`
  }

  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

/** Drop-in `xaxis.labels` for a category axis of ISO day strings. */
export const monthDayLabels = {
  rotate: 0,
  formatter: (value: string) => monthDay(value),
}


/*
    makeApexOptions with the Monitor page's date axis applied.

    The alternative was pasting the same datetimeFormatter override into ten chart
    files and hoping the eleventh remembered. This is the seam that already exists --
    every one of those charts already calls makeApexOptions -- so it costs one import
    per file and cannot be forgotten by a chart that uses it.

    DefaultApexOptions itself is left alone deliberately: it is shared with charts
    outside the Monitor page, and restyling those was not part of the request.
*/
export function monitorApexOptions(
  overrides: ApexOptions,
  chartType?: ApexChartType,
): ApexOptions {
  // chartType stays optional because makeApexOptions defaults it; several callers
  // rely on that and requiring it here would be a gratuitous edit to each of them.
  const merged = chartType === undefined
    ? makeApexOptions(overrides)
    : makeApexOptions(overrides, chartType)
  return {
    ...merged,
    xaxis: {
      ...merged.xaxis,
      labels: {
        ...merged.xaxis?.labels,
        datetimeFormatter: MONTH_DAY_DATETIME,
      },
    },
  }
}
